import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
    QUERY_GET_STORE,
    MUTATION_UPDATE_STORE,
} from "../../apollo/module-operations/store";
import { MUTATION_GET_IMAGEURL } from "../../apollo/module-operations/file_upload";
import useMeta from "../../base/hooks/useMeta.js";
import useNotification from "../../base/hooks/components/useNotification";
import {
    Button,
    IconButton,
    Input,
    LoadingProgress,
    Modal,
    Text,
    Thumbnail,
} from "@/ui";
import Icon from "./Icon";
import { formatFileUploadFileName } from "utils";
import styles from "./Medias.module.scss";

const SearchIcon = () => <Icon icon="search" />;
const PlusIcon = () => <Icon icon="plus" />;
const CopyIcon = () => <Icon icon="copy" />;
const DeleteIcon = () => <Icon icon="delete" />;
const GlobeIcon = () => <i className="fas fa-globe" />;

export function Medias({ isOpen, toggle, defaultSelected, onChange }) {
    const { user } = useMeta();
    const [medias, setMedias] = useState([]);
    const [query, setQuery] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(null);
    const fileInputRef = useRef(null);
    const searchTimerRef = useRef(null);
    const notify = useNotification();

    const { data } = useQuery(QUERY_GET_STORE, {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
        },
        skip: user.loading || !user.accountId || !user.storeId,
        onCompleted: res => {
            setMedias(res?.getStore?.medias || []);
        },
    });

    const [updateStore] = useMutation(MUTATION_UPDATE_STORE, {
        onCompleted: () => {
            notify(
                selectedMedia
                    ? "Media deleted successfully"
                    : "Media uploaded successfully",
                "success",
            );
            setSelectedMedia(null);
        },
        onError: () => {
            notify("Some error ocurred. Please, try again later.", "error");
        },
        refetchQueries: [
            {
                query: QUERY_GET_STORE,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
            "getStore",
        ],
    });

    const updateMedias = newMedias => {
        setIsLoading(true);

        setMedias(newMedias);
        updateStore({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...data.getStore,
                    medias: newMedias,
                },
            },
        }).finally(() => setIsLoading(false));
    };

    const confirmDelete = () => {
        updateMedias(
            data.getStore.medias.filter(media => media !== selectedMedia),
        );

        if (onChange) {
            onChange("");
        }

        setShowDeleteConfirm(false);
    };

    const handleSearch = e => {
        const value = e.target.value;
        setQuery(value);

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(() => {
            searchTimerRef.current = null;

            const filteredMedias = (data?.getStore?.medias || []).filter(
                media => media.toLowerCase().includes(value.toLowerCase()),
            );

            setMedias(filteredMedias);
        }, 800);
    };

    const copyUrl = (e, url) => {
        e.stopPropagation();
        navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        setTimeout(() => setCopiedUrl(null), 1000);
    };

    const [generatePresignedUrl] = useMutation(MUTATION_GET_IMAGEURL);

    const handleUpload = e => {
        if (e.target.files.length === 0) {
            return;
        }

        setIsLoading(true);

        const file = e.target.files[0];
        const nameFormat = formatFileUploadFileName(
            file.name,
            user.accountId,
            user.storeId,
        );

        generatePresignedUrl({
            variables: {
                input: {
                    file_name: nameFormat,
                    file_type: file.type,
                },
            },
        })
            .then(async resp => {
                const { url, headers } = resp.data.uploadFile;
                const config = {
                    headers: {
                        "Content-Type": file.type,
                        ...headers.reduce(
                            (prev, { key, value }) => ({
                                ...prev,
                                [key]: value,
                            }),
                            {},
                        ),
                    },
                };
                const upload = await axios.put(url, file, config);

                if (upload.status === 200) {
                    const media = `${process.env.REACT_APP_STATIC_CONTENT_BASE_URL}${nameFormat}`;
                    const newMedias = [media, ...medias];

                    setQuery("");
                    updateMedias(newMedias);

                    if (onChange) {
                        onChange(media);
                    }
                } else {
                    notify(
                        "Error uploading media. Please, try again later.",
                        "error",
                    );
                }
            })
            .catch(err => {
                console.error(err);

                notify(
                    "Error preparing media. Please, try again later.",
                    "error",
                );
            })
            .finally(() => {
                setIsLoading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            });
    };

    useEffect(() => {
        if (data) {
            setIsLoading(false);
        }
    }, [data]);

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, []);

    return (
        <>
            <Modal
                assistiveDescription="Select or upload a media file."
                onOpenChange={open => {
                    if (!open) {
                        toggle();
                    }
                }}
                open={Boolean(isOpen)}
                size="lg"
                title="Medias"
            >
                <div className={styles.body}>
                    <div className={styles.toolbar}>
                        <Input
                            className={`${styles.search} ${styles.searchField}`}
                            disabled={isLoading}
                            iconLeft={<SearchIcon />}
                            label="Search"
                            labelBehavior="inline"
                            name="search"
                            onChange={handleSearch}
                            type="search"
                            value={query}
                        />
                        <div className={styles.uploadWrap}>
                            <Button
                                disabled={isLoading}
                                iconLeft={<PlusIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                type="button"
                                variant="primary"
                            >
                                Add new
                            </Button>
                            <input
                                accept="image/*"
                                className={styles.hiddenFile}
                                disabled={isLoading}
                                multiple={false}
                                onChange={handleUpload}
                                ref={fileInputRef}
                                type="file"
                            />
                        </div>
                    </div>

                    <div className={styles.divider} />

                    {isLoading ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading medias"
                            />
                        </div>
                    ) : null}

                    {!isLoading && (!medias || medias.length === 0) ? (
                        <Text tone="secondary" variant="body-md">
                            There aren&apos;t medias.
                        </Text>
                    ) : null}

                    {!isLoading && medias?.length > 0 ? (
                        <div className={styles.galleryScroll}>
                            <div className={styles.gallery} role="radiogroup">
                                {medias.map(media => {
                                    const name = media.split("/").reverse()[0];
                                    const selected = defaultSelected === media;

                                    return (
                                        <figure
                                            aria-checked={selected}
                                            className={`${styles.card} ${
                                                onChange
                                                    ? styles.cardSelectable
                                                    : ""
                                            } ${
                                                selected
                                                    ? styles.cardSelected
                                                    : ""
                                            }`}
                                            key={media}
                                            onClick={() => {
                                                if (onChange) {
                                                    onChange(media);
                                                    toggle();
                                                }
                                            }}
                                            role="radio"
                                        >
                                            <img alt="" src={media} />
                                            <figcaption
                                                className={styles.caption}
                                            >
                                                <span
                                                    className={styles.fileName}
                                                >
                                                    {name}
                                                </span>
                                                <div
                                                    className={
                                                        styles.cardActions
                                                    }
                                                >
                                                    <IconButton
                                                        aria-label={`Copy ${name}`}
                                                        onClick={e =>
                                                            copyUrl(e, media)
                                                        }
                                                        size="sm"
                                                        type="button"
                                                        variant="tertiary"
                                                    >
                                                        <CopyIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label={`Delete ${name}`}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setSelectedMedia(
                                                                media,
                                                            );
                                                            setShowDeleteConfirm(
                                                                true,
                                                            );
                                                        }}
                                                        size="sm"
                                                        type="button"
                                                        variant="tertiary"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                                {copiedUrl === media ? (
                                                    <span
                                                        className={
                                                            styles.copied
                                                        }
                                                    >
                                                        Copied!
                                                    </span>
                                                ) : null}
                                            </figcaption>
                                        </figure>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            </Modal>

            <Modal
                description="Are you sure you want delete this media?"
                footer={
                    <div className={styles.modalActions}>
                        <Button
                            loading={isLoading}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isLoading}
                            onClick={() => setShowDeleteConfirm(false)}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) setShowDeleteConfirm(false);
                }}
                open={showDeleteConfirm}
                title="Delete media"
            >
                {null}
            </Modal>
        </>
    );
}

function MediaInput({ name, value, onChange, invalid, ...props }) {
    const [isOpen, setIsOpen] = useState(false);

    const handleChangeMedia = url => {
        onChange({
            target: {
                name,
                value: url,
            },
        });
    };

    const openBrowse = event => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
    };

    const url = value ?? "";
    const canPreview = /^https?:\/\//i.test(url);

    return (
        <>
            <div className={styles.mediaInput}>
                <Input
                    error={invalid}
                    fullWidth
                    iconLeft={<GlobeIcon />}
                    iconRight={
                        <button
                            className={styles.browseInside}
                            onClick={openBrowse}
                            onMouseDown={event => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            type="button"
                        >
                            Browse
                        </button>
                    }
                    label={props.label || "Media URL"}
                    name={name}
                    onChange={onChange}
                    value={url}
                />
                {canPreview ? (
                    <Thumbnail
                        alt=""
                        bordered
                        className={styles.preview}
                        size="md"
                        src={url}
                        variant="product-image"
                    />
                ) : null}
            </div>
            <Medias
                defaultSelected={value}
                isOpen={isOpen}
                onChange={handleChangeMedia}
                toggle={() => setIsOpen(false)}
            />
        </>
    );
}

export default MediaInput;
